/**
 * Pure color utilities used by the calendar grid to derive per-event card
 * styling from a Subject Area's `ColorHex`. No React, no Tailwind — these
 * produce inline `style` objects because the palette colors aren't named
 * tokens in the Tailwind theme.
 *
 * Algorithm at a glance:
 *  - Card body: low-alpha tint of the subject hex (`rgba(r,g,b,0.18)`) so the
 *    card reads as categorized but doesn't fight the text.
 *  - Left border: the saturated hex itself, thick — this is the at-a-glance
 *    "what subject" cue.
 *  - Text color: chosen via WCAG contrast against the tinted card body so
 *    text is always legible regardless of swatch hue.
 *
 * Falls back to the existing gray surface tokens when the hex is missing or
 * malformed, preserving the Phase 1 visual baseline for un-categorized events.
 */

export interface Rgb {
  r: number;
  g: number;
  b: number;
}

export interface Hsl {
  h: number;
  s: number;
  l: number;
}

export interface EventCardStyle {
  /** `backgroundColor` value — either an `rgba(...)` tint or the fallback. */
  background: string;
  /** Used for the saturated left-border accent. */
  borderColor: string;
  /** WCAG-aware text color against `background`. */
  color: string;
}

const HEX_RE = /^#([0-9a-fA-F]{6})$/;

/** Gray fallback used for events with no matching SubjectArea. Matches the
 *  pre-Slice-5 baseline so un-categorized events keep their muted look. */
export const FALLBACK_CARD_STYLE: EventCardStyle = {
  // Same surface token used by `bg-surface` (slate-800-ish). Keeping it hex
  // here means the inline style doesn't fight `bg-surface` if both end up
  // applied during partial-render states.
  background: "rgba(255, 255, 255, 0.04)",
  borderColor: "rgba(255, 255, 255, 0.10)",
  color: "#ffffff",
};

export function parseHex(hex: string): Rgb | null {
  if (typeof hex !== "string") return null;
  const match = HEX_RE.exec(hex.trim());
  if (!match) return null;
  const value = match[1];
  return {
    r: parseInt(value.slice(0, 2), 16),
    g: parseInt(value.slice(2, 4), 16),
    b: parseInt(value.slice(4, 6), 16),
  };
}

export function rgbToHsl({ r, g, b }: Rgb): Hsl {
  const rN = r / 255;
  const gN = g / 255;
  const bN = b / 255;
  const max = Math.max(rN, gN, bN);
  const min = Math.min(rN, gN, bN);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case rN:
        h = (gN - bN) / d + (gN < bN ? 6 : 0);
        break;
      case gN:
        h = (bN - rN) / d + 2;
        break;
      default:
        h = (rN - gN) / d + 4;
    }
    h *= 60;
  }
  return { h, s, l };
}

function hueToRgbComponent(p: number, q: number, t: number): number {
  let tn = t;
  if (tn < 0) tn += 1;
  if (tn > 1) tn -= 1;
  if (tn < 1 / 6) return p + (q - p) * 6 * tn;
  if (tn < 1 / 2) return q;
  if (tn < 2 / 3) return p + (q - p) * (2 / 3 - tn) * 6;
  return p;
}

export function hslToHex({ h, s, l }: Hsl): string {
  const hN = (((h % 360) + 360) % 360) / 360;
  let r: number;
  let g: number;
  let b: number;
  if (s === 0) {
    r = g = b = l;
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hueToRgbComponent(p, q, hN + 1 / 3);
    g = hueToRgbComponent(p, q, hN);
    b = hueToRgbComponent(p, q, hN - 1 / 3);
  }
  const toHex = (n: number) =>
    Math.round(n * 255)
      .toString(16)
      .padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/** WCAG 2.x relative luminance. Input is sRGB 0..255; output is 0..1. */
export function relativeLuminance({ r, g, b }: Rgb): number {
  const linearize = (c: number) => {
    const cs = c / 255;
    return cs <= 0.03928 ? cs / 12.92 : Math.pow((cs + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * linearize(r) + 0.7152 * linearize(g) + 0.0722 * linearize(b);
}

export function contrastRatio(a: Rgb, b: Rgb): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const lighter = Math.max(la, lb);
  const darker = Math.min(la, lb);
  return (lighter + 0.05) / (darker + 0.05);
}

const WHITE: Rgb = { r: 255, g: 255, b: 255 };
const NEAR_BLACK: Rgb = { r: 17, g: 17, b: 17 };

/**
 * Picks white or near-black to maximize contrast against `bgHex`. Returns the
 * higher-contrast option (we don't enforce the 4.5:1 minimum strictly — for
 * very mid-luminance backgrounds neither option clears the bar, but picking
 * the higher-ratio option is the best we can do without changing the bg).
 */
export function contrastTextColor(bgHex: string): "#ffffff" | "#111111" {
  const bg = parseHex(bgHex);
  if (!bg) return "#ffffff";
  const whiteRatio = contrastRatio(WHITE, bg);
  const blackRatio = contrastRatio(NEAR_BLACK, bg);
  return whiteRatio >= blackRatio ? "#ffffff" : "#111111";
}

/**
 * Blend `fg` over `bg` at the given alpha (0..1). Used to compute the visible
 * tint of an alpha-blended card body against the dark page background, which
 * is what the user actually sees and what we need to pick text color against.
 */
function blendOver(fg: Rgb, bg: Rgb, alpha: number): Rgb {
  return {
    r: Math.round(fg.r * alpha + bg.r * (1 - alpha)),
    g: Math.round(fg.g * alpha + bg.g * (1 - alpha)),
    b: Math.round(fg.b * alpha + bg.b * (1 - alpha)),
  };
}

/** Approximation of the calendar page background (`bg-page` ≈ slate-900). */
const PAGE_BG: Rgb = { r: 15, g: 23, b: 42 };
const TINT_ALPHA = 0.22;

/**
 * Main API: derive the inline card style for an event's subject-area color.
 * Returns the gray fallback when `colorHex` is null/undefined/invalid so
 * un-categorized events keep their muted appearance.
 */
export function eventCardStyle(
  colorHex: string | null | undefined,
): EventCardStyle {
  if (!colorHex) return FALLBACK_CARD_STYLE;
  const rgb = parseHex(colorHex);
  if (!rgb) return FALLBACK_CARD_STYLE;

  const background = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${TINT_ALPHA})`;
  // Compose the visible color of the tinted card body against the page bg so
  // we pick text against what the user actually perceives, not against the
  // alpha-channel value alone.
  const effectiveBg = blendOver(rgb, PAGE_BG, TINT_ALPHA);
  const effectiveBgHex = hslToHex(rgbToHsl(effectiveBg));

  return {
    background,
    borderColor: colorHex,
    color: contrastTextColor(effectiveBgHex),
  };
}
